// src/components/Login.jsx

import React, { useState, useContext, useEffect } from "react";
import { Eye, EyeOff } from "react-feather";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { fetchWithAuth, loginUser } from "../utils/api";
import "./Login.css";

const Login = () => {
  // ─── Hooks ─────────────────────────────────────────────────────────────
  const [userId, setUserId]                   = useState("");
  const [password, setPassword]               = useState("");
  const [showPassword, setShowPassword]       = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetModal, setShowResetModal]   = useState(false);
  const [newPassword, setNewPassword]         = useState("placeholder");
  const [confirmNewPassword, setConfirmNewPassword] = useState("placeholder");
  const [errorMessage, setErrorMessage]       = useState("");
  const { login } = useContext(AuthContext);
  const navigate  = useNavigate();

  // Clear modal fields when it opens
  useEffect(() => {
    if (showResetModal) {
      const timeout = setTimeout(() => {
        setNewPassword("");
        setConfirmNewPassword("");
        setShowNewPassword(false);
        setShowConfirmPassword(false);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [showResetModal]);

  // ─── Mobile detection & early return ────────────────────────────────────
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
    .test(typeof navigator === "undefined" ? "" : navigator.userAgent);

  if (isMobileDevice) {
    return (
      <div className="mobile-incompatible">
        <h2>Device Not Supported</h2>
        <p>
          This device is not compatible with this site. Please use a laptop or
          desktop to continue.
        </p>
      </div>
    );
  }

  // ─── Handlers ───────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    try {
      const data = await loginUser({ userId, password });

      if (data.isDefaultPassword) {
        setShowResetModal(true);
        return;
      }

      await login({ userId, password }, navigate);
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage("Incorrect username or password.");
    }
  };

  // Password validation flags
  const hasUppercase   = /[A-Z]/.test(newPassword);
  const hasNumber      = /\d/.test(newPassword);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
  const passwordsMatch = newPassword === confirmNewPassword;

  const handlePasswordReset = async () => {
    if (!hasUppercase || !hasNumber || !hasSpecialChar || !passwordsMatch) {
      alert("Please meet all password requirements.");
      return;
    }
    if (newPassword === "password123") {
      alert("New password cannot be 'password123'.");
      return;
    }

    try {
      await fetchWithAuth("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, newPassword }),
      });

      alert("Password updated successfully!");
      await login({ userId, password: newPassword }, navigate);
      setShowResetModal(false);
      navigate("/dashboard");
    } catch (error) {
      console.error("❌ Password reset error:", error);
      alert("Error updating password.");
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-content">
        <h2 className="login-title">Login</h2>

        <form className="login-form" onSubmit={handleSubmit}>
          <label>UserName:</label>
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
          />

          <label className="password-label">
            Password:
            {errorMessage && (
              <span className="password-error-label"> (Incorrect Password)</span>
            )}
          </label>
          <div className={`password-input-wrapper ${errorMessage ? "shake" : ""}`}>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setShowPassword(false)}
              required
              className={errorMessage ? "error-border" : ""}
            />
            <span
              className="password-toggle-icon"
              onMouseDown={() => setShowPassword(true)}
              onMouseUp={() => setShowPassword(false)}
              onMouseLeave={() => setShowPassword(false)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </span>
          </div>

          <button type="submit">Log In</button>
        </form>
      </div>

      {showResetModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Reset Password</h2>
            <p>You are using a default password. Please change it to continue.</p>

            <label>New Password:</label>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <span
                className="password-toggle-icon"
                onMouseDown={() => setShowNewPassword(true)}
                onMouseUp={() => setShowNewPassword(false)}
                onMouseLeave={() => setShowNewPassword(false)}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </span>
            </div>

            <ul className="password-rules">
              <li className={hasUppercase ? "valid" : "invalid"}>One uppercase letter</li>
              <li className={hasNumber ? "valid" : "invalid"}>One number</li>
              <li className={hasSpecialChar ? "valid" : "invalid"}>One special character</li>
            </ul>

            <label>Confirm New Password:</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
              />
              <span
                className="password-toggle-icon"
                onMouseDown={() => setShowConfirmPassword(true)}
                onMouseUp={() => setShowConfirmPassword(false)}
                onMouseLeave={() => setShowConfirmPassword(false)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </span>
            </div>

            {confirmNewPassword && (
              <p className={passwordsMatch ? "match" : "no-match"}>
                {passwordsMatch ? "✅ Passwords match" : "❌ Passwords do not match"}
              </p>
            )}

            <button onClick={handlePasswordReset} className="modal-button">
              Update Password
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
